// framework/client/components/DownstreamServices.js
import { ComponentBase } from "../core/ComponentBase.js";

export class DownstreamServices extends ComponentBase {
    static styles = `
        .downstream-service-card {
            border-radius: var(--ab-radius-md);
            box-shadow: var(--ab-shadow-xs);
            transition: transform var(--ab-t-base) ease, box-shadow var(--ab-t-base) ease;
        }
        .downstream-service-card:hover {
            transform: translateY(-4px);
            box-shadow: var(--ab-shadow-sm);
        }
        .downstream-service-card button:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }
        .status-badge {
            font-size: var(--ab-text-xs);
            font-weight: var(--ab-weight-semibold);
            border-radius: var(--ab-radius-full);
            padding: 0.2rem 0.5rem;
        }
        .status-provisioned {
            color: var(--bs-success-text-emphasis);
            background-color: var(--bs-success-bg-subtle);
            border: 1px solid var(--bs-success-border-subtle);
        }
        .status-not_provisioned {
            color: var(--bs-warning-text-emphasis);
            background-color: var(--bs-warning-bg-subtle);
            border: 1px solid var(--bs-warning-border-subtle);
        }
    `;

    constructor(config) {
        super(config);
        this.services = [];
        this.applyStyles();
    }

    async initAfterLogin() {
        await this.loadServices();
    }

    async loadServices() {
        try {
            this.services = await this.appClient.rpcClient.call('EcosystemRpc.getDownstreamServicesStatus');
            this.render();
        } catch (error) {
            this.appClient.notifier.error(this.t("Could not load downstream services status."));
            this.log.error("Error loading downstream services:", error);
        }
    }

    async handleProvision(serviceName) {
        const btn = this.container.querySelector(`[data-action="provision"][data-service="${serviceName}"]`);
        if (!btn) return;

        btn.disabled = true;
        btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span> ${this.t('Provisioning...')}`;

        try {
            const result = await this.appClient.rpcClient.call('EcosystemRpc.provisionDownstreamService', { serviceName });
            if (result.success) {
                this.appClient.notifier.success(this.t("Successfully provisioned '{serviceName}'.", { serviceName }));
                await this.loadServices(); // Refresh the list
            } else {
                throw new Error(result.error || "Provisioning failed for an unknown reason.");
            }
        } catch (error) {
            this.appClient.notifier.error(this.t("Failed to provision '{serviceName}': {message}", { serviceName, message: error.message }));
            btn.disabled = false;
            btn.innerHTML = `<i class="bi bi-plus-circle-fill me-2"></i> ${this.t('Provision')}`;
        }
    }

    async handleLogin(serviceName) {
        const btn = this.container.querySelector(`[data-action="login"][data-service="${serviceName}"]`);
        if (!btn) return;

        btn.disabled = true;
        btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span> ${this.t('Generating Link...')}`;

        try {
            const result = await this.appClient.rpcClient.call('EcosystemRpc.generateDownstreamLoginLink', { serviceName });
            if (result.loginUrl) {
                window.open(result.loginUrl, '_blank');
            } else {
                throw new Error("Server did not return a login URL.");
            }
        } catch (error) {
            this.appClient.notifier.error(this.t("Failed to generate login link for '{serviceName}': {message}", { serviceName, message: error.message }));
        } finally {
            btn.disabled = false;
            btn.innerHTML = `<i class="bi bi-box-arrow-up-right me-2"></i> ${this.t('Login')}`;
        }
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
        }
        this.container.innerHTML = `
            <div class="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3>${this.t('Downstream Service Integrations')}</h3>
                    <p class="text-muted">${this.t('Manage connections to other AppBuilder services like Omnix.')}</p>
                </div>
            </div>
            <div id="services-list" class="row g-3"></div>
        `;

        const listContainer = this.container.querySelector('#services-list');
        if (this.services.length === 0) {
            listContainer.innerHTML = `
                <div class="col-12">
                    <div class="ab-empty">
                        <i class="bi bi-inbox ab-empty-icon"></i>
                        <div class="ab-empty-title">${this.t('No downstream services found')}</div>
                        <div class="ab-empty-body">${this.t('No downstream services are configured for this application.')}</div>
                    </div>
                </div>
            `;
            return this.container;
        }

        this.services.forEach(service => {
            const card = document.createElement('div');
            card.className = 'col-md-6 col-lg-4';
            
            let actionButton;
            if (service.isProvisioned) {
                actionButton = `<button class="btn btn-primary w-100" data-action="login" data-service="${service.name}"><i class="bi bi-box-arrow-up-right me-2"></i> ${this.t('Login as Admin')}</button>`;
            } else {
                actionButton = `<button class="btn btn-success w-100" data-action="provision" data-service="${service.name}"><i class="bi bi-plus-circle-fill me-2"></i> ${this.t('Provision Account')}</button>`;
            }

            const description = service.isProvisioned
                ? this.t("This subscription is linked to a dedicated '{serviceName}' account.", { serviceName: service.name })
                : this.t("Create a dedicated '{serviceName}' account for this subscription.", { serviceName: service.name });

            card.innerHTML = `
                <div class="card h-100 downstream-service-card">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex justify-content-between align-items-start">
                            <h5 class="card-title text-capitalize">${service.name}</h5>
                            <span class="badge status-badge ${service.isProvisioned ? 'status-provisioned' : 'status-not_provisioned'}">
                                ${service.isProvisioned ? this.t('Provisioned') : this.t('Not Provisioned')}
                            </span>
                        </div>
                        <p class="card-text text-muted small mt-2 flex-grow-1">
                            ${this.escapeHtml(description)}
                        </p>
                        ${actionButton}
                    </div>
                </div>
            `;
            listContainer.appendChild(card);
        });

        this.container.querySelectorAll('[data-action="provision"]').forEach(btn => {
            btn.addEventListener('click', () => this.handleProvision(btn.dataset.service));
        });
        this.container.querySelectorAll('[data-action="login"]').forEach(btn => {
            btn.addEventListener('click', () => this.handleLogin(btn.dataset.service));
        });

        return this.container;
    }
}